import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {getFormTableSwitcherStatus} from 'notebook/structured-tables/action-functions/form-table-switcher/FormTableSwitcherStateActions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  CreateStructuredTableProps,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOrFormOutsideNotebook, {
  TableOrFormRenderOptions,
  TableOrFormRenderOptionsType,
} from 'notebook/structured-tables/StructuredTableOrFormOutsideNotebook';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {MethodComponent} from 'procedures/ProcedureDomainTypes';
import {BenchlingState} from 'redux-stores/reduceApp';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

interface ConnectedProps {
  renderType: TableOrFormRenderOptionsType;
}

interface ProcedureConfigTableProps<
  T extends MethodComponent,
  A extends GenericStructuredTableAdapter<TTableMetadata, TRowMetadata>,
  TTableMetadata,
  TRowMetadata,
> {
  onChangeDetails: (newDetails: Array<T>) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  tableCid: string;
  tableAdapter: A;
  createStructuredTableProps: CreateStructuredTableProps<TTableMetadata>;
  rowToDetail: (row: StructuredTableRow<TRowMetadata>, columns: Array<StructuredTableColumn>) => T;
  locked?: boolean;
  table: StructuredTable<TTableMetadata, TRowMetadata>;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Presents a way to edit multiple values of the same type (e.g., "all parameters" or "all equipments")
 * using either a form or a table.
 */
function UnconnectedProcedureConfigTable<
  T extends MethodComponent,
  A extends GenericStructuredTableAdapter<TTableMetadata, TRowMetadata>,
  TTableMetadata,
  TRowMetadata,
>(props: ProcedureConfigTableProps<T, A, TTableMetadata, TRowMetadata> & ConnectedProps): JSX.Element {
  const {onChangeDetails, rowToDetail, locked, onChangeErrorState} = props;
  const [errorState, setErrorState] = useState<boolean>(false);

  const updateTable = useCallback(
    (newTable: StructuredTable<TTableMetadata, TRowMetadata>) => {
      const newDetails = newTable.rows.map((row) => rowToDetail(row, newTable.columns));
      onChangeDetails(newDetails);
    },
    [onChangeDetails, rowToDetail]
  );

  useEffect(() => {
    onChangeErrorState?.(errorState);
  }, [errorState, onChangeErrorState]);

  return (
    <div>
      <StructuredTableOrFormOutsideNotebook
        shouldShowAs={props.renderType}
        structuredTable={props.table}
        cid={props.tableCid}
        updateHandler={updateTable}
        onComputedTableChanged={(computedTable) => {
          setErrorState(doesEvaluatedTableHaveAnyErrors(computedTable));
        }}
        includeUnsubmittedStatus={true}
        hideColumnHeaderIcons={true}
        hasPrimarySamples={false}
        isCollapsible={false}
        locked={locked}
        canRename={false}
        dataTestAttrs={props.dataTestAttrs}
      />
    </div>
  );
}

function mapStateToProps(state: BenchlingState, props: {tableCid: string}): ConnectedProps {
  return {
    renderType: getFormTableSwitcherStatus(state, props.tableCid) ?? TableOrFormRenderOptions.TABLE,
  };
}

const ProcedureConfigTable = connect(mapStateToProps)(UnconnectedProcedureConfigTable);
export default ProcedureConfigTable;
